import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MapPin, Globe, Instagram, Facebook, Clock, Award, AlertCircle } from 'lucide-react';
import api from '../api';
import ServiceCard from '../components/ServiceCard';
import ContactForm from '../components/ContactForm';
import Seo from '../components/Seo';
import { providerPath } from '../utils/slug';
import './ProfiloProvider.css';

// Costruisce un URL assoluto da un valore che può essere già un link o un handle.
const webUrl = (v) => (/^https?:\/\//i.test(v) ? v : `https://${v}`);
const socialUrl = (v, base) =>
  /^https?:\/\//i.test(v) ? v : `https://${base}/${String(v).replace(/^@/, '')}`;

function ProfiloProvider() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [provider, setProvider] = useState(null);
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProvider = async () => {
      try {
        const res = await api.get(`/servizi/provider/by-slug/${slug}`);
        setProvider(res.data.provider);
        setServizi(res.data.servizi || []);
      } catch (err) {
        setError('Provider non trovato');
        console.error('Error loading provider:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProvider();
  }, [slug]);

  // SEO: porta l'URL allo slug canonico pulito (es. da /provider/4 o link legacy).
  useEffect(() => {
    if (!provider) return;
    const canonico = providerPath(provider);
    if (canonico !== '/cerca' && location.pathname !== canonico) {
      navigate(canonico, { replace: true });
    }
  }, [provider, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="profilo-page loading">
        <div className="spinner" />
        Caricamento...
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="profilo-page error">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>{error || 'Provider non trovato'}</h2>
          <p>Torna alla ricerca per trovare altri provider</p>
          <Link to="/cerca" className="btn btn-primary">
            Torna alla Ricerca
          </Link>
        </div>
      </div>
    );
  }

  // I servizi mobili (intrattenimento/food) operano su più aree: mostriamo le zone
  // coperte al posto della sede singola, poco significativa per loro.
  const mobile = ['animazione', 'food'].includes(provider.tipo_provider);

  // Dati strutturati schema.org (LocalBusiness) per la SEO.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: provider.nome_attivita || provider.referente || 'Fornitore',
    ...(provider.descrizione_attivita ? { description: provider.descrizione_attivita.slice(0, 300) } : {}),
    ...(provider.avatar_url ? { image: provider.avatar_url } : {}),
    ...(provider.sito_web ? { url: webUrl(provider.sito_web) } : {}),
    ...((provider.citta || provider.regione) ? {
      address: {
        '@type': 'PostalAddress',
        ...(provider.citta ? { addressLocality: provider.citta } : {}),
        ...(provider.regione ? { addressRegion: provider.regione } : {}),
        ...(provider.cap ? { postalCode: provider.cap } : {}),
        addressCountry: 'IT',
      },
    } : {}),
  };

  return (
    <div className="profilo-page">
      <Seo
        title={provider.nome_attivita}
        description={(provider.descrizione_attivita || '').slice(0, 155)}
        image={provider.avatar_url}
        type="profile"
        canonical={providerPath(provider)}
        jsonLd={jsonLd}
      />
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span> / </span>
        <Link to="/cerca">Ricerca</Link>
        <span> / </span>
        <span>{provider.nome_attivita}</span>
      </div>

      {/* Provider Header */}
      <div className="provider-header-section">
        <div className="header-content">
          <div className="header-avatar">
            <img
              src={provider.avatar_url || '/default-avatar.svg'}
              alt={provider.nome_attivita}
              className="avatar"
              onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.svg'; }}
            />
          </div>
          <div className="header-info">
            <h1>{provider.nome_attivita || provider.referente || 'Fornitore'}</h1>
            {provider.referente && provider.nome_attivita && (
              <p className="header-referente">di {provider.referente}</p>
            )}
            <div className="header-meta">
              {mobile && provider.zone_coperte ? (
                <span className="meta-chip">
                  <MapPin size={16} />
                  {provider.zone_coperte}
                </span>
              ) : (provider.citta || provider.regione) && (
                <span className="meta-chip">
                  <MapPin size={16} />
                  {[provider.citta, provider.regione].filter(Boolean).join(', ')}
                </span>
              )}
              <span className="meta-chip">
                <Award size={16} />
                {servizi.length} {servizi.length === 1 ? 'servizio' : 'servizi'}
              </span>
              {provider.anni_esperienza != null && (
                <span className="meta-chip">
                  <Clock size={16} />
                  {provider.anni_esperienza} anni di esperienza
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="profilo-container">
        {/* Main Content */}
        <div className="profilo-main">
          {/* Descrizione: affiancata a contatti e form (sidebar) */}
          {provider.descrizione_attivita && (
            <section className="about-section">
              <h2>Chi siamo</h2>
              <p className="description">{provider.descrizione_attivita}</p>
            </section>
          )}

          {/* Services Section */}
          <section className="services-section">
            <h2>I nostri servizi ({servizi.length})</h2>
            {servizi.length > 0 ? (
              <div className="services-grid">
                {servizi.map((servizio) => (
                  <ServiceCard
                    key={servizio.id}
                    servizio={{ ...servizio, nome_attivita: provider.nome_attivita, citta: provider.citta }}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Questo provider non ha ancora pubblicato servizi.</p>
              </div>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="profilo-sidebar">
          {/* Contact Info */}
          <div className="contact-info-card">
            <h3>Contatti</h3>
            {provider.referente && (
              <div className="contact-item">
                <span className="contact-label">Referente:</span> {provider.referente}
              </div>
            )}
            {provider.sito_web && (
              <div className="contact-item">
                <Globe size={18} />
                <a href={webUrl(provider.sito_web)} target="_blank" rel="noreferrer">Sito web</a>
              </div>
            )}
            {provider.instagram && (
              <div className="contact-item">
                <Instagram size={18} />
                <a href={socialUrl(provider.instagram, 'instagram.com')} target="_blank" rel="noreferrer">Instagram</a>
              </div>
            )}
            {provider.facebook && (
              <div className="contact-item">
                <Facebook size={18} />
                <a href={socialUrl(provider.facebook, 'facebook.com')} target="_blank" rel="noreferrer">Facebook</a>
              </div>
            )}
            {mobile && provider.zone_coperte ? (
              <div className="contact-item">
                <MapPin size={18} />
                <span>Zone servite: {provider.zone_coperte}</span>
              </div>
            ) : (provider.citta || provider.provincia) && (
              <div className="contact-item">
                <MapPin size={18} />
                <span>
                  {[provider.citta, provider.provincia, provider.regione].filter(Boolean).join(', ')}
                  {provider.cap ? ` (${provider.cap})` : ''}
                </span>
              </div>
            )}
            {provider.orari && (
              <div className="contact-item">
                <Clock size={18} />
                <span>{provider.orari}</span>
              </div>
            )}
          </div>

          {/* Dettagli aggiuntivi */}
          {(provider.anni_esperienza || (provider.zone_coperte && !mobile) || provider.note) && (
            <div className="contact-info-card">
              <h3>Dettagli</h3>
              {provider.anni_esperienza != null && (
                <div className="contact-item">
                  <Award size={18} />
                  <span>{provider.anni_esperienza} anni di esperienza</span>
                </div>
              )}
              {!mobile && provider.zone_coperte && (
                <div className="contact-item">
                  <MapPin size={18} />
                  <span>Zone servite: {provider.zone_coperte}</span>
                </div>
              )}
              {provider.note && <p className="provider-note">{provider.note}</p>}
            </div>
          )}

          {/* Contact Form */}
          <ContactForm
            providerId={provider.id}
            providerName={provider.nome_attivita}
            messaggiAttivi={provider.messaggi_attivi}
          />
        </div>
      </div>
    </div>
  );
}

export default ProfiloProvider;
